/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreHandlerLocale - kCore's country / regional-format lookups.
 * Split out of kCoreHandler.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kCore keeps every member as a same-signature delegating member.
 */

import { getCoreUtils } from "./kCoreErrorRegistry";
import type { kCore } from "./kCoreHandler";

export class kCoreHandlerLocale {
    /** Two-letter language code -> its default culture ("zh-hant" -> Traditional Chinese "zh-TW"). */
    private static readonly CountryRegions: { [Code: string]: string } = {
        "af": "af-ZA", "am": "am-ET", "ar": "ar-SA", "az": "az-AZ", "be": "be-BY", "bg": "bg-BG", "bn": "bn-BD", "bs": "bs-BA",
        "ca": "ca-ES", "cs": "cs-CZ", "cy": "cy-GB", "da": "da-DK", "de": "de-DE", "el": "el-GR", "en": "en-US", "es": "es-ES",
        "et": "et-EE", "eu": "eu-ES", "fa": "fa-IR", "fi": "fi-FI", "fil": "fil-PH", "fr": "fr-FR", "gl": "gl-ES", "gu": "gu-IN",
        "he": "he-IL", "hi": "hi-IN", "hr": "hr-HR", "hu": "hu-HU", "hy": "hy-AM", "id": "id-ID", "is": "is-IS", "it": "it-IT",
        "ja": "ja-JP", "jv": "jv-ID", "ka": "ka-GE", "kk": "kk-KZ", "km": "km-KH", "kn": "kn-IN", "ko": "ko-KR", "lo": "lo-LA",
        "lt": "lt-LT", "lv": "lv-LV", "mk": "mk-MK", "ml": "ml-IN", "mn": "mn-MN", "mr": "mr-IN", "ms": "ms-MY", "nb": "nb-NO",
        "ne": "ne-NP", "nl": "nl-NL", "no": "no-NO", "pa": "pa-IN", "pl": "pl-PL", "pt": "pt-PT", "ro": "ro-RO", "ru": "ru-RU",
        "si": "si-LK", "sk": "sk-SK", "sl": "sl-SI", "sq": "sq-AL", "sr": "sr-RS", "sv": "sv-SE", "sw": "sw-KE", "ta": "ta-IN",
        "te": "te-IN", "th": "th-TH", "tr": "tr-TR", "uk": "uk-UA", "ur": "ur-PK", "uz": "uz-UZ", "vi": "vi-VN", "zh": "zh-CN",
        "zh-hant": "zh-TW",
    };

    public static GetFullCountryAndRegionFromCountry = (Self: typeof kCore, TwoLetterCountryCode: string): string => {
        if (getCoreUtils().KErrorMode) { return TwoLetterCountryCode; }
        try {
            const Code: string = TwoLetterCountryCode.toLowerCase();
            if (Object.prototype.hasOwnProperty.call(kCoreHandlerLocale.CountryRegions, Code)) {
                return kCoreHandlerLocale.CountryRegions[Code];
            }
            return TwoLetterCountryCode; // Return original if not matched
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerLocale.GetFullCountryAndRegionFromCountry");
            return TwoLetterCountryCode;
        }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.GetLocalDataFormat - its gate, try/catch and handler label "kCore.GetLocalDataFormat" stay verbatim, so error reports are unchanged.
    public static GetLocalDataFormat = (Self: typeof kCore, CountryCode: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let TempCurrFormat: any = null;
            CountryCode = CountryCode.toLowerCase();
            if (CountryCode.length === 2) {
                CountryCode = Self.GetFullCountryAndRegionFromCountry(CountryCode).toLowerCase();
            }
            for (let i = 0; i < Self._localeDateFormat.length; i++) {
                if (Self._localeDateFormat[i].BC47 === CountryCode) {
                    TempCurrFormat = Self._localeDateFormat[i];
                    if ([3081, 10249, 4105, 9225, 6153, 8201, 5129, 13321,
                        7177, 11273, 2057, 1033, 3084, 1081, 1065, 1097,
                    ].indexOf(TempCurrFormat.LCID) > -1) {
                        TempCurrFormat.T12 = true;
                    } else {
                        TempCurrFormat.T12 = false;
                    }
                    break;
                }
            }
            Self.NumberandDateFormat = TempCurrFormat;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.GetLocalDataFormat"); }
    };
}
